/**
 * @voltxampere/ui — DragTool (Sprint 13)
 *
 * Mouse-driven move for selected components. State machine:
 *
 *   idle → pointerdown on component → dragging
 *   dragging → pointermove → live preview (mutate circuit)
 *   dragging → pointerup → revert preview, push MoveCommand /
 *               BatchCommand to UndoStack, idle
 *   dragging → onLeave / Esc → revert preview, idle
 *
 * Snap-to-grid happens only on the *final* pointerup position so
 * the cursor follows the user smoothly during the drag and lands
 * on the nearest grid intersection on release.
 *
 * If the drag delta stays under `MICRO_DRAG_THRESHOLD` pixels the
 * tool treats it as a click and skips the MoveCommand entirely.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { BatchCommand, MoveCommand } from "../commands/index.js";
export const DRAG_GRID_SIZE = 16;
export const MICRO_DRAG_THRESHOLD = 4; // world units
export class DragTool {
    id = "drag";
    cursor = "move";
    state = { type: "idle" };
    onPointerDown(e, ctx) {
        if (e.button !== 0)
            return;
        const circuit = ctx.circuit.value;
        if (!circuit)
            return;
        const world = ctx.worldFromEvent(e);
        const hit = ctx.hitTester.hit(world);
        if (hit?.kind !== "component")
            return;
        // Selection: keep existing if hit was selected, else replace.
        let dragIds;
        if (ctx.selectedNodes.value.includes(hit.id)) {
            dragIds = ctx.selectedNodes.value;
        }
        else {
            const additive = e.shiftKey || e.metaKey || e.ctrlKey;
            dragIds = additive ? [...ctx.selectedNodes.value, hit.id] : [hit.id];
            ctx.selectedNodes.value = dragIds;
        }
        const originals = new Map();
        for (const id of dragIds) {
            const c = circuit.components.find((cmp) => cmp.id === id);
            if (c)
                originals.set(id, { ...c.position });
        }
        this.state = {
            type: "dragging",
            startWorld: world,
            componentIds: dragIds,
            originalPositions: originals,
        };
        try {
            ctx.canvas.setPointerCapture(e.pointerId);
        }
        catch {
            // happy-dom may not implement pointer capture
        }
        ctx.invalidate();
    }
    onPointerMove(e, ctx) {
        if (this.state.type !== "dragging")
            return;
        const circuit = ctx.circuit.value;
        if (!circuit)
            return;
        const world = ctx.worldFromEvent(e);
        const dx = world.x - this.state.startWorld.x;
        const dy = world.y - this.state.startWorld.y;
        const ids = this.state.componentIds;
        const originals = this.state.originalPositions;
        ctx.circuit.value = {
            ...circuit,
            components: circuit.components.map((c) => {
                const orig = originals.get(c.id);
                if (!orig || !ids.includes(c.id))
                    return c;
                return { ...c, position: { x: orig.x + dx, y: orig.y + dy } };
            }),
        };
        // Sprint 58 — snap-glow drag-tool integration (S57 partial
        // closure). While dragging, compute the nearest grid snap target
        // for the dragged anchor (representative position) and surface
        // it as the polish overlay glow. Intensity = 1 at grid origin,
        // 0 at the half-cell distance.
        if (ctx.snapGlow !== undefined) {
            const firstId = ids[0];
            if (firstId !== undefined) {
                const orig = originals.get(firstId);
                if (orig !== undefined) {
                    const targetWorld = { x: orig.x + dx, y: orig.y + dy };
                    const snapped = ctx.viewport.snapToGrid(targetWorld, DRAG_GRID_SIZE);
                    const distance = Math.hypot(targetWorld.x - snapped.x, targetWorld.y - snapped.y);
                    const threshold = DRAG_GRID_SIZE / 2;
                    if (distance < threshold) {
                        const intensity = Math.max(0, 1 - distance / threshold);
                        ctx.snapGlow.set({ point: snapped, intensity });
                    }
                    else {
                        ctx.snapGlow.clear();
                    }
                }
            }
        }
        ctx.invalidate();
    }
    onPointerUp(e, ctx) {
        // Sprint 58 — clear snap glow on release regardless of whether
        // the move qualified as a real drag (cancel + commit both).
        ctx.snapGlow?.clear();
        if (this.state.type !== "dragging")
            return;
        const circuit = ctx.circuit.value;
        if (!circuit) {
            this.state = { type: "idle" };
            return;
        }
        const world = ctx.worldFromEvent(e);
        const dx = world.x - this.state.startWorld.x;
        const dy = world.y - this.state.startWorld.y;
        const moved = Math.abs(dx) >= MICRO_DRAG_THRESHOLD || Math.abs(dy) >= MICRO_DRAG_THRESHOLD;
        const ids = this.state.componentIds;
        const originals = this.state.originalPositions;
        // Revert the live preview to originals so the undo stack sees
        // a clean baseline before executing MoveCommand.
        const reverted = revertToOriginals(circuit, ids, originals);
        if (!moved) {
            ctx.circuit.value = reverted;
            this.state = { type: "idle" };
            this.releaseCapture(e, ctx);
            return;
        }
        const commands = [];
        for (const id of ids) {
            const orig = originals.get(id);
            if (!orig)
                continue;
            const final = ctx.viewport.snapToGrid({ x: orig.x + dx, y: orig.y + dy }, DRAG_GRID_SIZE);
            if (final.x === orig.x && final.y === orig.y)
                continue;
            commands.push(new MoveCommand(id, orig, final));
        }
        if (commands.length === 0) {
            ctx.circuit.value = reverted;
            this.state = { type: "idle" };
            this.releaseCapture(e, ctx);
            return;
        }
        const first = commands[0];
        const cmd = commands.length === 1 && first
            ? first
            : new BatchCommand(commands, `${String(commands.length)} öge taşındı`);
        ctx.circuit.value = ctx.undoStack.execute(cmd, reverted);
        this.state = { type: "idle" };
        this.releaseCapture(e, ctx);
        ctx.invalidate();
    }
    onLeave(ctx) {
        ctx.snapGlow?.clear();
        if (this.state.type === "dragging") {
            const circuit = ctx.circuit.value;
            if (circuit) {
                ctx.circuit.value = revertToOriginals(circuit, this.state.componentIds, this.state.originalPositions);
            }
            this.state = { type: "idle" };
            ctx.invalidate();
        }
    }
    /** Test surface — peek at the internal state. */
    get isDragging() {
        return this.state.type === "dragging";
    }
    releaseCapture(e, ctx) {
        try {
            if (ctx.canvas.hasPointerCapture(e.pointerId)) {
                ctx.canvas.releasePointerCapture(e.pointerId);
            }
        }
        catch {
            // ignore
        }
    }
}
function revertToOriginals(circuit, ids, originals) {
    return {
        ...circuit,
        components: circuit.components.map((c) => {
            const orig = originals.get(c.id);
            if (!orig || !ids.includes(c.id))
                return c;
            return { ...c, position: orig };
        }),
    };
}
//# sourceMappingURL=drag.js.map